import React, { useEffect, useState } from "react";
import { Modal, Typography, Flex, Button, Tooltip } from "antd";
import {
  BellIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { ALERT_STATUS, ALERT_TYPE } from "../../types";
import { formatDate } from "../../utils/helpers";
import { useUpdateAlertStatusMutation } from "../../actions/alertsInQuery";

const { Text, Title } = Typography;

interface AlertModalProps {
  open: boolean;
  onClose: () => void;
  alertId: string;
  type: ALERT_TYPE;
  deviceName: string;
  trapsiteName: string;
  trapsiteAddress: string;
  createdAt: Date;
  status: ALERT_STATUS;
  description: string;
  message: string;
  /**
   * Why the read status cannot be changed here. Set, the toggle is disabled and
   * this is its tooltip; the alert itself still opens.
   */
  statusLockedReason?: string;
}

const AlertModal: React.FC<AlertModalProps> = ({
  open,
  onClose,
  alertId,
  type,
  deviceName,
  trapsiteName,
  trapsiteAddress,
  createdAt,
  status,
  description,
  message,
  statusLockedReason,
}) => {
  // Reset per alert too: the table keeps this modal mounted and hands it the
  // next alert, whose status can equal the last one's before it was toggled.
  const [currentStatus, setCurrentStatus] = useState(status);
  useEffect(() => {
    setCurrentStatus(status);
  }, [status, alertId]);

  const [updateStatus, { isLoading }] = useUpdateAlertStatusMutation();

  const toggleStatus = async () => {
    const next = currentStatus === "unread" ? "read" : "unread";
    const result = await updateStatus({ id: alertId, status: next });
    if (!("error" in result)) setCurrentStatus(next);
  };

  const toggle = (
    <Button
      type="primary"
      disabled={!!statusLockedReason}
      // Left to antd's own disabled look when locked; these colours
      // would otherwise make it read as clickable.
      style={
        statusLockedReason
          ? undefined
          : currentStatus === "read"
            ? {
                background: "#FCF1F0",
                color: "#F1635F",
                borderColor: "#F1635F",
              }
            : { background: "#0ea5e9", borderColor: "#0ea5e9" }
      }
      loading={isLoading}
      onClick={toggleStatus}
    >
      {currentStatus === "unread" ? "Mark as Read" : "Mark as Unread"}
    </Button>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closeIcon={null}
      width={700}
      styles={{
        content: {
          borderRadius: ".5rem",
          border: "1px solid #0C4A6E",
        },
      }}
    >
      <Flex vertical gap="16px" className="py-2">
        <Flex vertical gap="8px">
          <Text>
            {deviceName || "—"} - {trapsiteName || "—"}
          </Text>
          <Text>{trapsiteAddress || "—"}</Text>
          <Text>{formatDate(createdAt)}</Text>
          <Flex align="center" gap="8px">
            {type === "maintenance" && (
              <ExclamationTriangleIcon className="w-6 h-6 text-[#F97316]" />
            )}
            {type === "detection" && (
              <MagnifyingGlassIcon className="w-6 h-6 text-green-500" />
            )}
            {type === "notification" && (
              <BellIcon className="w-6 h-6 text-[#0C4A6E]" />
            )}
            <Title
              level={4}
              style={{ margin: 0, fontSize: "24px", fontWeight: 500 }}
            >
              {message}
            </Title>
          </Flex>
          {message !== description && <Text>{description}</Text>}
        </Flex>
        <Flex justify="flex-end" gap="16px" className="mt-2">
          <Button type="text" className="font-medium" onClick={onClose}>
            Close
          </Button>

          {/* Only wrapped when locked: an antd Tooltip keeps describing its
              child after the title goes, which would read the old lock reason
              out on an alert that can be toggled. */}
          {statusLockedReason ? (
            <Tooltip title={statusLockedReason}>{toggle}</Tooltip>
          ) : (
            toggle
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};

export default AlertModal;
